import { ReactNode, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPut, apiDelete } from "../lib/api";
import {
  useCurrentPlan,
  useLibraryEntry,
  useClients,
  useSessionDraft,
  useUnreadMessages,
  useFamilyBilling,
  useSessions,
  useGoals,
  useExerciseHistory,
  queryKeys,
} from "../lib/queries";
import type { FamilyClientListItem, SessionDraft } from "../lib/queries";
import { LoadingState, ErrorState } from "../components/Loading";
import { PageShell } from "../components/PageShell";
import {
  PlanElement,
  PlanBody,
  parsePlanBody,
  describePrescription,
  describeLoad,
  hasPrescribedLoad,
  loadValueInUnit,
  formatWeight,
  convertWeight,
  measureOf,
  formatSeconds,
  groupOf,
  groupRange,
  repetitionsFor,
  describeSessionLength,
} from "../lib/planElements";
import { playTick, playGo, playTimerEnd, playFanfare } from "../lib/sounds";
import { useAccountStatus } from "../lib/queries";
import { PlanDetail } from "../components/PlanDetail";
import { ClientGoals, GoalRow, formatTargetDate } from "../components/ClientGoals";
import { PlanRequestStatus } from "../components/PlanRequestStatus";
import { ProgressChart } from "../components/ProgressChart";
import { SkaterLoginForm } from "../components/SkaterLoginForm";
import { SkaterLoginDetails } from "../components/SkaterLoginDetails";
import { WorkspaceTabs, WorkspacePanel, WorkspaceTab, useWorkspaceTab, useIsNarrow } from "../components/Workspace";
import { initials } from "../lib/initials";
import { SessionHistory } from "../components/SessionHistory";
import { LibraryCues } from "../components/LibraryCues";
import { CheckIn } from "../components/CheckIn";
import { LastTime } from "../components/LastTime";
import { CheckinAnswers, toAnswers } from "../lib/checkin";
import { historyFor } from "../lib/exerciseHistory";

interface PlanRow {
  id: string;
  client_id: string;
  title: string;
  status: string;
  elements: string;
}

interface LibraryEntry {
  video_url: string | null;
}

interface LoggedExercise {
  elementIndex: number;
  /** Which set of that exercise this is, 0-based. One row per set, not per exercise. */
  setIndex: number;
  status: "completed" | "skipped";
  reps: number | null;
  weight: number | null;
  weightUnit: "lb" | "kg" | "bodyweight" | null;
  /** Seconds actually held, for a time-measured exercise. */
  durationSeconds: number | null;
  flagged: boolean;
  flagNote: string | null;
}

/** A missing or zero `sets` still means one set was done, not none. */
/** Kept for the places that only have an element; the circuit-aware count is `repetitionsFor`. */
function setsFor(element: { sets?: number } | undefined): number {
  return Math.max(1, element?.sets ?? 1);
}

/**
 * There is no invented rest between exercises any more.
 *
 * An empty rest field used to mean two different things depending on which one it was: nothing at all
 * between sets, and a full-screen 60-second countdown between exercises, from a constant the coach
 * never chose and could not see (UX review F61). So Sam prescribed a pace he did not intend, and Maya
 * sat through an unexplained minute three times a session -- which a nine-year-old's attention does not
 * survive.
 *
 * Both fields now behave the same way when left empty: no rest. A rest an athlete waits through is a
 * real instruction and belongs to the coach, so it is only ever one they actually gave. The builder
 * prefills a suggested value so the common case is still a sensible pause rather than none.
 */

/** How long the between-exercise celebration holds the screen before the rest timer takes over. */
const CELEBRATION_MS = 1400;

/**
 * Duck-typed on `status` rather than `instanceof ApiError`. An instanceof check ties this to one
 * particular module instance of the class, which isn't guaranteed to be the same object the error was
 * constructed from -- so a "no plan yet" 404 could fall through to the generic error branch and show
 * the athlete a failure message instead of the empty state.
 */
function isNotFound(error: unknown): boolean {
  if (error && typeof error === "object" && "status" in error) {
    return (error as { status: unknown }).status === 404;
  }
  return error instanceof Error && /not found/i.test(error.message);
}

/** Athlete-facing guided walkthrough: RPE scale, rest timer, skip, flag-issue (PRD §7). */
export function AthleteWalkthrough() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const planQuery = useCurrentPlan<PlanRow>(clientId);
  const plan = planQuery.data;

  /**
   * Whether this caller may actually write, from the same list that renders the "Read-only" badge on
   * the family home.
   *
   * The server decides this per the guardian-on-behalf state machine (architecture §2c): the moment a
   * minor gets their own login, the guardian reverts to read-only. QA found this page still offering
   * "Start session" and "Add goal" to a guardian in exactly that state -- the API refuses both with 403,
   * verified live, but a button that always fails is its own defect and makes the app look broken
   * rather than deliberately restricted.
   *
   * Defaults to editable while the list is loading, so the athlete's own view never flickers into a
   * read-only state it will immediately leave.
   */
  const clientsQuery = useClients<FamilyClientListItem[]>();
  const thisClient = clientsQuery.data?.find((c) => c.id === clientId);
  const canWrite = thisClient ? thisClient.editable : true;

  const [currentIndex, setCurrentIndex] = useState(0);
  /**
   * Which set of the current exercise the athlete is on.
   *
   * The walkthrough used to log one row per exercise, which could say "you did squats" but not "you
   * held up for two sets and fell off on the third" -- and that pattern is most of why a coach reads a
   * session at all.
   */
  const [currentSet, setCurrentSet] = useState(0);
  const [log, setLog] = useState<LoggedExercise[]>([]);
  /**
   * One RPE for the whole session, asked at the end.
   *
   * Per set it meant rating effort ten times in a workout -- tedious, and less honest: the number you
   * give while catching your breath mid-session is not the one you would give having finished it.
   */
  const [sessionRpe, setSessionRpe] = useState<number | null>(null);
  const [weightUnit, setWeightUnit] = useState<"lb" | "kg" | "bodyweight">("lb");
  /**
   * The account's preferred unit, used as the starting point for each set.
   *
   * Only a default: the unit is recorded per set, so switching the preference later never reinterprets
   * what was already logged.
   */
  const accountStatus = useAccountStatus();
  const preferredUnit = accountStatus.data?.weight_unit ?? "lb";
  const [reps, setReps] = useState("");
  const [weight, setWeight] = useState("");
  /** Opened by the athlete when they used a load the coach didn't prescribe. */
  const [weightPromptOpen, setWeightPromptOpen] = useState(false);
  const [flagged, setFlagged] = useState(false);
  const [flagNote, setFlagNote] = useState("");
  /**
   * Whether this set's report has already been sent to the coach on its own.
   *
   * Guards against sending it twice: the athlete may send it explicitly and then press Complete, and
   * Complete sends it as a backstop for the case where they never pressed either button.
   */
  const [flagSent, setFlagSent] = useState(false);
  /** Set when the session ended because something was wrong, so the closing screen doesn't celebrate. */
  const [stoppedEarly, setStoppedEarly] = useState(false);
  /**
   * Exercises the athlete has reported hurting on, by their index into the plan's flat element list.
   *
   * "Tell my coach and keep going" used to send the report and then leave the athlete on the exact
   * exercise they had just said hurt -- so the same movement was immediately offered again, with
   * nothing stopping them from being asked to repeat it (27 Sep debug notes). Once an exercise is here,
   * it is treated as off-limits for the rest of the session: skipped the moment it is flagged, and, if
   * it recurs as one exercise among several in a circuit, skipped automatically on every later round
   * too -- the OTHER exercises in that circuit are untouched, since nothing was reported about them.
   */
  const [injuredElementIndexes, setInjuredElementIndexes] = useState<Set<number>>(new Set());
  const [restSecondsLeft, setRestSecondsLeft] = useState<number | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);
  /**
   * Countdown for a time-measured exercise ("hold a plank for 30 seconds").
   *
   * Null when not running. A hold is the one prescription the athlete cannot self-pace by counting, so
   * the app times it rather than leaving them to guess.
   */
  const [holdSecondsLeft, setHoldSecondsLeft] = useState<number | null>(null);
  // The walkthrough used to drop straight into exercise 1, so the athlete could never see what the
  // whole session held before committing to it. It now opens on an overview they explicitly start from.
  /**
   * Where in the session we are.
   *
   * `countdown` and `celebrating` are deliberately their own phases rather than flags on the working
   * screen: they take the whole screen, and an athlete mid-set should never see a countdown overlay
   * competing with the exercise they are doing.
   */
  /**
   * `checkin` comes between the overview and the countdown, every workout. `rested` is where an athlete
   * who said something hurts and chose not to train ends up: no session, just the report.
   */
  const [phase, setPhase] = useState<
    "overview" | "checkin" | "rested" | "countdown" | "working" | "resting" | "celebrating" | "rating" | "done"
  >("overview");
  /**
   * The check-in answered before this workout. It goes into every draft save and onto the logged
   * session, so a resumed workout keeps it instead of asking again.
   */
  const [checkin, setCheckin] = useState<CheckinAnswers | null>(null);
  const [countdown, setCountdown] = useState(3);

  /** 3 - 2 - 1 - GO, then straight into the first set. */
  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown <= 0) {
      const id = setTimeout(() => setPhase("working"), 600);
      return () => clearTimeout(id);
    }
    const id = setTimeout(() => {
      setCountdown((n) => n - 1);
      if (countdown - 1 <= 0) playGo();
      else playTick();
    }, 800);
    return () => clearTimeout(id);
  }, [phase, countdown]);

  useEffect(() => {
    if (restSecondsLeft === null) return;
    if (restSecondsLeft <= 0) {
      // The athlete is usually not looking at the screen during a rest, which is exactly why this beeps.
      playTimerEnd();
      endRest();
      return;
    }
    const id = setTimeout(() => setRestSecondsLeft((s) => (s !== null ? s - 1 : null)), 1000);
    return () => clearTimeout(id);
  }, [restSecondsLeft]);

  useEffect(() => {
    if (holdSecondsLeft === null) return;
    if (holdSecondsLeft <= 0) {
      playTimerEnd();
      return;
    }
    const id = setTimeout(() => setHoldSecondsLeft((s) => (s !== null ? s - 1 : null)), 1000);
    return () => clearTimeout(id);
  }, [holdSecondsLeft]);

  /**
   * There is no `beforeunload` guard here any more, deliberately.
   *
   * There used to be one, because nothing was written until "Finish session" and leaving discarded the
   * whole workout. It was the browser's own "Leave site? Changes you made may not be saved" -- generic
   * wording, in OS chrome, two equally-weighted buttons, no mention of what was actually at stake, and a
   * nine-year-old taps one at random (UX review F71). Worse, it never fired for the failure that actually
   * happens: a backgrounded tab being evicted while the phone is locked.
   *
   * Every completed set is now saved server-side as it happens, so leaving is no longer destructive and
   * a dialog that says otherwise would be a lie. The most that can be lost is the set being typed.
   */

  // A new exercise clears any timer left over from the last one.
  useEffect(() => {
    setHoldSecondsLeft(null);
  }, [currentIndex]);

  const planBody = plan ? parsePlanBody(plan.elements) : { elements: [], groups: [] };
  const elements = planBody.elements;
  /** The circuit the athlete is in the middle of, if any -- what turns "sets" into "rounds" on screen. */
  const currentGroup = groupOf(planBody, elements[currentIndex]);
  /** How many goes this exercise gets: its own sets, or the rounds of the circuit it is in. */
  const repetitions = repetitionsFor(planBody, currentIndex);
  const current = elements[currentIndex];

  /**
   * Carries a reported injury forward through every later round of a circuit.
   *
   * `stopExerciseDueToInjury` only ever records ONE round as skipped -- the one the athlete was on when
   * they reported it -- and lets the circuit's own round-by-round logic decide whether more rounds
   * follow, exactly as if that round had been completed normally. This is what makes each of those later
   * rounds skip the same exercise again automatically, so the athlete is never asked to do it a second
   * time (27 Sep debug notes), while the circuit's OTHER exercises keep arriving normally each round.
   *
   * Deliberately not marked as flagged again here: the report already reached the coach the moment it
   * was made, and repeating it every round it comes back around would just be noise.
   */
  useEffect(() => {
    if (phase !== "working" || !current) return;
    if (!injuredElementIndexes.has(currentIndex)) return;
    stopExerciseDueToInjury();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, currentIndex, injuredElementIndexes]);

  const videoQuery = useLibraryEntry<LibraryEntry>(current?.libraryEntryId);
  /** Recent sessions in full, for "last time" on the working screen. Empty while loading or on error. */
  const exerciseHistory = useExerciseHistory(clientId).data ?? [];
  const currentVideoUrl = videoQuery.data?.video_url ?? null;

  useEffect(() => {
    setVideoFailed(false);
  }, [current?.libraryEntryId]);

  /**
   * Every set starts with the prescription already in the reps box.
   *
   * The number used to be a placeholder, which looks identical to a filled-in value -- so the normal
   * case (did exactly what was asked) submitted nothing at all, and the coach's session record read
   * "Completed, nothing recorded" for 12 of 15 entries (UX review F74). Seeding it here rather than in
   * `resetExerciseForm` covers every way the position changes: next set, next exercise, next circuit
   * round, and a skip.
   *
   * Keyed on the position, not on the value, so it never fights with someone typing a different number.
   */
  useEffect(() => {
    setReps(current?.reps ? String(current.reps) : "");
    /**
     * Converted into whichever unit the athlete is actually entering in, not copied verbatim.
     *
     * The old version copied the coach's raw number straight into the box regardless of unit -- so a
     * coach on lb prescribing "10" pre-filled a kg-preferring athlete's weight box with "10" as well,
     * sitting right next to a "Coach said: 10 kg" label that had made the same mistake. The athlete,
     * seeing an already-filled-in number that matched what the label said, had no reason to think
     * anything was wrong, and logged 10 kg when 10 lb was meant -- wrong in the dangerous direction for
     * a child loading a bar. `loadValueInUnit` converts properly; bodyweight has nothing to prefill,
     * and the box is hidden for it regardless.
     */
    const seededWeight = current && weightUnit !== "bodyweight" ? loadValueInUnit(current, weightUnit) : null;
    setWeight(seededWeight !== null ? formatWeight(seededWeight) : "");
    setWeightPromptOpen(false);
    // `weightUnit` deliberately isn't a dependency: this seeds once per position (matching the reps
    // seed above), reading whatever unit is already selected at that moment -- resetExerciseForm sets
    // it fresh for each new exercise in the same update. Adding it here would re-seed, and silently
    // overwrite, a weight the athlete had already typed just because they clicked a different unit chip
    // on the set they are still on.
  }, [currentIndex, currentSet, current?.reps, current?.load, current?.loadValue, current?.loadUnit]);

  /**
   * The session so far, on the server.
   *
   * Saved after every completed or skipped set, so the workout survives the phone locking, the tab being
   * evicted, a stray back-navigation or a flat battery (UX review F70). Failures are swallowed rather
   * than surfaced: this is a background safety net, and interrupting a nine-year-old mid-workout with
   * "could not save progress" would be worse than the risk it warns about. The set in hand is never at
   * stake -- it lives in `log` either way, and the next save carries it.
   */
  const saveDraft = useMutation({
    mutationFn: (body: {
      planId: string;
      currentIndex: number;
      currentSet: number;
      exercises: LoggedExercise[];
      checkin: CheckinAnswers | null;
    }) =>
      apiPut(`/clients/${clientId}/session-draft`, body),
    onError: () => {},
  });

  const draftQuery = useSessionDraft(clientId);
  /**
   * The workout to offer to pick back up.
   *
   * Only when it is against the plan currently published: a coach who has published a new programme
   * since has changed what the athlete should be doing, and resuming into the old one would log sets
   * against exercises that are no longer prescribed. Only when something was actually logged, too --
   * resuming an empty session is just starting one.
   */
  const draft = draftQuery.data?.draft ?? null;
  const resumable: SessionDraft | null =
    draft && plan && draft.plan_id === plan.id && draft.exercises.length > 0 ? draft : null;

  const discardDraft = useMutation({
    mutationFn: () => apiDelete(`/clients/${clientId}/session-draft`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.sessionDraft(clientId ?? "") }),
  });

  const logSession = useMutation({
    mutationFn: (exercises: LoggedExercise[]) =>
      // sessionRpe rides along with the sets: one rating for the whole workout, collected on the last
      // screen rather than ten times along the way.
      apiPost("/sessions", { clientId, planId: plan!.id, exercises, sessionRpe, checkin }),
    onSuccess: async () => {
      setPhase("done");
      // No fanfare for a session that ended because something hurt -- congratulating someone on stopping
      // in pain reads as the app not having understood what just happened.
      if (!stoppedEarly) playFanfare();
      // The server clears the draft as part of writing the session; this is the client's own belt and
      // braces, because a stale draft would offer to resume the session that was just logged.
      queryClient.setQueryData(queryKeys.sessionDraft(clientId ?? ""), { draft: null });
      apiDelete(`/clients/${clientId}/session-draft`).catch(() => {});
      // The history below has to pick the new session up. It reads through gsi1, which is eventually
      // consistent, so the just-written session may not be in the index yet -- the invalidation makes
      // the list refetch, and any lag self-corrects on the next one rather than being wrong forever.
      await queryClient.invalidateQueries({ queryKey: queryKeys.sessions(clientId ?? "") });
    },
  });

  /**
   * Tells the coach something is wrong, immediately, on its own.
   *
   * The report used to be an attribute of a completed session, so it arrived only if the athlete pushed
   * on through every remaining exercise and rated the workout at the end -- and the more serious the
   * problem, the less likely that was (UX review F72). It is still recorded on the set as well; that is
   * the record of what happened during the workout, this is the alert.
   */
  const sendFlag = useMutation({
    mutationFn: (input: { note: string; sessionEnded: boolean }) =>
      apiPost(`/clients/${clientId}/flags`, {
        note: input.note || null,
        planId: plan?.id ?? null,
        elementIndex: currentIndex,
        exerciseName: current?.name ?? null,
        setIndex: currentSet,
        sessionEnded: input.sessionEnded,
      }),
    onSuccess: () => setFlagSent(true),
  });

  /**
   * "Something hurts" from the check-in, sent as a flag so it reaches the coach's "Needs your attention"
   * even if no session follows. It shows on the athlete's page and nowhere else: no email, no message
   * (decided 2026-10-04).
   */
  const sendCheckinFlag = useMutation({
    mutationFn: (input: { answers: CheckinAnswers; resting: boolean }) =>
      apiPost(`/clients/${clientId}/flags`, {
        source: "checkin",
        painAreas: input.answers.painAreas,
        note: input.answers.painNote || null,
        planId: plan?.id ?? null,
        sessionEnded: input.resting,
      }),
  });

  /** Past the check-in, into the workout. */
  function trainAfterCheckin(answers: CheckinAnswers) {
    setCheckin(answers);
    // In the background, and not blocking the workout: the check-in also rides on the session itself,
    // so a failed send here still reaches the coach if they finish.
    if (answers.pain === "yes") sendCheckinFlag.mutate({ answers, resting: false });
    beginWorkout();
  }

  /**
   * Something hurts and they're not training today. The flag is the only record of that, since no
   * session follows, so this waits for it and stays on the check-in if it fails.
   */
  function restAfterCheckin(answers: CheckinAnswers) {
    setCheckin(answers);
    sendCheckinFlag.mutate({ answers, resting: true }, { onSuccess: () => setPhase("rested") });
  }

  function resetExerciseForm() {
    setReps("");
    setWeight("");
    setWeightPromptOpen(false);
    setFlagged(false);
    setFlagNote("");
    // A new set is a new report: leaving this set would make the next issue look already-sent and the
    // backstop on Complete would skip it.
    setFlagSent(false);
    sendFlag.reset();
    setHoldSecondsLeft(null);
    setWeightUnit(preferredUnit);
  }

  /**
   * Switches the athlete's own unit chip, converting whatever they had already typed rather than
   * leaving it stale in the old unit or discarding it.
   *
   * Without this, switching units mid-set would leave the exact inconsistency this whole fix is about,
   * just moved: the "Coach said" line above converts correctly, but the number the athlete is about to
   * submit would sit there unconverted in the newly-selected unit -- e.g. "10" still showing under a
   * freshly-clicked "kg" chip, having been typed while "lb" was selected. Converting it here instead of
   * re-seeding from the prescription is deliberate: the athlete's own answer is preserved and corrected,
   * not silently replaced by the coach's target number.
   */
  function handleWeightUnitChange(unit: "lb" | "kg" | "bodyweight") {
    if (unit !== "bodyweight" && weightUnit !== "bodyweight" && weight.trim()) {
      const numeric = Number(weight);
      if (!Number.isNaN(numeric)) {
        setWeight(formatWeight(convertWeight(numeric, weightUnit, unit)));
      }
    }
    setWeightUnit(unit);
  }

  /**
   * Whether the rest currently running is between sets of one exercise or between two exercises.
   *
   * The two look different on purpose: a between-sets rest names the set you are about to do, while a
   * between-exercises rest previews the next exercise with its cues and demo video, so the pause is
   * useful rather than just elapsed.
   */
  const [restingBetweenSets, setRestingBetweenSets] = useState(false);

  function beginWorkout() {
    setPhase("countdown");
    setCountdown(3);
    playTick();
  }

  /** Ends a rest early, or when it runs out. Either way the next thing is work. */
  function endRest() {
    setRestSecondsLeft(null);
    setPhase("working");
  }

  /**
   * Moves to the next thing, choosing the rest that belongs to that transition.
   *
   * Three kinds of transition, not two, now that a circuit exists:
   *
   * - **Another set of the same exercise** -- a straight exercise with sets left. Between-set rest gets
   *   no default: it is a thing the coach either prescribed or did not, and inventing 60s between every
   *   set would pace a session nobody asked for.
   * - **The next exercise in the circuit** -- same round, so no celebration and no round rest; a circuit
   *   moves briskly and stopping to congratulate somebody five times a round would be absurd.
   * - **The next round, or the next exercise entirely** -- back to the top of the circuit, or onward.
   *
   * A round *is* a set as far as logging is concerned, so `currentSet` counts rounds inside a circuit
   * and nothing about how sessions are stored changes.
   */
  /**
   * Where the athlete will be once this set is behind them.
   *
   * Split out from `advanceAfterSet` because the saved session has to say where to resume, and the state
   * setters that move the walkthrough don't report back the value they set. Computing it here means the
   * position written to the server and the position the screen moves to come from one piece of logic
   * rather than two that can drift apart -- which, with circuits in the mix, they certainly would.
   */
  function nextPosition(justFinishedIndex: number, wasLastSet: boolean): { index: number; set: number } {
    const element = elements[justFinishedIndex];
    const group = groupOf(planBody, element);

    if (group) {
      const range = groupRange(planBody, group.id);
      const isLastInRound = !range || justFinishedIndex >= range.last;
      // Next exercise in the round, same round number.
      if (!isLastInRound) return { index: justFinishedIndex + 1, set: currentSet };
      // Round done, more to come: back to the top of the circuit.
      if (!wasLastSet) return { index: range ? range.first : justFinishedIndex, set: currentSet + 1 };
      // Circuit finished: out the other side, starting at its first set.
      return { index: range ? range.last + 1 : justFinishedIndex + 1, set: 0 };
    }

    if (!wasLastSet) return { index: justFinishedIndex, set: currentSet + 1 };
    return { index: justFinishedIndex + 1, set: 0 };
  }

  function advanceAfterSet(justFinishedIndex: number, wasLastSet: boolean) {
    const element = elements[justFinishedIndex];
    const group = groupOf(planBody, element);
    const next = nextPosition(justFinishedIndex, wasLastSet);

    if (group) {
      const range = groupRange(planBody, group.id);
      const isLastInRound = !range || justFinishedIndex >= range.last;

      if (!isLastInRound) {
        // Straight on to the next exercise in the round, keeping the round number.
        setCurrentIndex(next.index);
        const rest = element?.restAfterSeconds ?? 0;
        if (rest > 0) {
          setRestingBetweenSets(false);
          setRestSecondsLeft(rest);
          setPhase("resting");
        } else {
          setPhase("working");
        }
        return;
      }

      if (!wasLastSet) {
        // Round done, more to go: back to the top of the circuit for the next one.
        setCurrentIndex(next.index);
        setCurrentSet(next.set);
        const rest = group.restBetweenRoundsSeconds ?? 0;
        if (rest > 0) {
          setRestingBetweenSets(true);
          setRestSecondsLeft(rest);
          setPhase("resting");
        } else {
          setPhase("working");
        }
        return;
      }

      // The whole circuit is done. Out the other side, and worth celebrating.
      finishExerciseAndAdvance(next.index, element);
      return;
    }

    if (!wasLastSet) {
      setCurrentSet(next.set);
      const rest = element?.restBetweenSetsSeconds ?? null;
      if (rest && rest > 0) {
        setRestingBetweenSets(true);
        setRestSecondsLeft(rest);
        setPhase("resting");
      } else {
        setPhase("working");
      }
      return;
    }

    finishExerciseAndAdvance(next.index, element);
  }

  /** The celebration and the between-exercise rest, shared by a plain exercise and a finished circuit. */
  function finishExerciseAndAdvance(nextIndex: number, finished: PlanElement | undefined) {
    setCurrentIndex(nextIndex);
    setCurrentSet(0);

    // A short celebration between exercises, and a bigger one when the whole session is done.
    setPhase("celebrating");
    playFanfare();
    window.setTimeout(() => {
      if (nextIndex >= elements.length) {
        setPhase("rating");
        return;
      }
      // `?? 0`, matching the between-sets rest exactly -- see the note where DEFAULT_REST_SECONDS was.
      const rest = finished?.restAfterSeconds ?? 0;
      if (rest > 0) {
        setRestingBetweenSets(false);
        setRestSecondsLeft(rest);
        setPhase("resting");
      } else {
        setPhase("working");
      }
    }, CELEBRATION_MS);
  }



  /**
   * Writes the session so far to the server, with the position it will resume at.
   *
   * Called on the way out of every set rather than on a timer: a set is the natural unit of work here,
   * and saving on one means the worst case is retyping the set that was in progress when the phone
   * locked -- not the twenty minutes before it.
   */
  function persistProgress(exercises: LoggedExercise[], position: { index: number; set: number }) {
    if (!plan || !clientId) return;
    saveDraft.mutate({
      planId: plan.id,
      currentIndex: position.index,
      currentSet: position.set,
      exercises,
      checkin,
    });
  }

  /**
   * Sends the report on its own, and optionally ends the session there.
   *
   * Both buttons send immediately, because the whole failure being fixed is a report that waits for a
   * session that never gets finished (UX review F72). `sessionEnded` rides on the flag itself so the
   * coach can tell "it hurt but they carried on" from "it hurt and they stopped", which are very
   * different things to read on a Monday morning.
   *
   * "Keep going" no longer means "ask about this same exercise again a moment later" (27 Sep debug
   * notes): reporting one now retires it for the rest of the session, via `stopExerciseDueToInjury`.
   */
  function reportIssue(options: { stop: boolean }) {
    if (!flagSent) {
      sendFlag.mutate({ note: flagNote, sessionEnded: options.stop });
    }
    if (options.stop) {
      endSessionEarly();
    } else {
      stopExerciseDueToInjury();
    }
  }

  /**
   * Stops the workout where it is and logs what was actually done.
   *
   * The set in hand is recorded as skipped and keeps the flag, so the session record says where they got
   * to and why it ended. No RPE screen: asking someone who has just reported pain to rate how hard the
   * session felt is the wrong question at the wrong moment.
   */
  function endSessionEarly() {
    const stoppedOn: LoggedExercise = {
      elementIndex: currentIndex,
      setIndex: currentSet,
      status: "skipped",
      reps: null,
      weight: null,
      weightUnit: null,
      durationSeconds: null,
      flagged: true,
      flagNote: flagNote || null,
    };
    const finalLog = [...log, stoppedOn];
    setLog(finalLog);
    setStoppedEarly(true);
    setSessionRpe(null);
    logSession.mutate(finalLog);
  }

  /** Records one set and hands over to the transition logic, which owns which rest applies. */
  function completeSet(entry: LoggedExercise) {
    const nextLog = [...log, entry];
    setLog(nextLog);
    const wasLastSet = currentSet + 1 >= repetitions;
    const position = nextPosition(currentIndex, wasLastSet);
    // Backstop for someone who ticks the box, types, and just presses Complete: the report still leaves
    // the device now rather than waiting for a session that may never be finished.
    if (entry.flagged && !flagSent) {
      sendFlag.mutate({ note: entry.flagNote ?? "", sessionEnded: false });
    }
    resetExerciseForm();
    advanceAfterSet(currentIndex, wasLastSet);
    persistProgress(nextLog, position);
  }

  function handleComplete() {
    completeSet({
      elementIndex: currentIndex,
      setIndex: currentSet,
      status: "completed",
      reps: reps ? Number(reps) : null,
      // Bodyweight records no number at all -- a 0 would read as "lifted nothing", which is different.
      weight: weightUnit === "bodyweight" || !weight ? null : Number(weight),
      weightUnit,
      durationSeconds: measureOf(current) === "time" ? current.durationSeconds ?? null : null,
      flagged,
      flagNote: flagged ? flagNote || null : null,
    });
  }

  /**
   * Records every remaining set of the current exercise as skipped and advances past it -- shared by
   * the manual Skip button and by an injury report, so both leave exactly the same kind of trail in the
   * session log. `flagLastSkippedSet` is a parameter rather than read from `flagged` directly, so an
   * injury report (which always means yes) doesn't depend on that checkbox being in any particular
   * state at the moment this runs.
   */
  function skipRemainingSetsOfCurrentExercise(flagLastSkippedSet: boolean) {
    const remaining = repetitions - currentSet;
    const skipped: LoggedExercise[] = Array.from({ length: remaining }, (_, i) => ({
      elementIndex: currentIndex,
      setIndex: currentSet + i,
      status: "skipped" as const,
      reps: null,
      weight: null,
      weightUnit: null,
      durationSeconds: null,
      // The flag belongs to the moment they gave up, so it goes on that set rather than on all of them.
      flagged: i === 0 ? flagLastSkippedSet : false,
      flagNote: i === 0 && flagLastSkippedSet ? flagNote || null : null,
    }));

    const nextLog = [...log, ...skipped];
    setLog(nextLog);
    const position = nextPosition(currentIndex, true);
    resetExerciseForm();
    // Skipping ends the exercise, so it takes the between-exercises path like a finished last set.
    advanceAfterSet(currentIndex, true);
    persistProgress(nextLog, position);
  }

  /**
   * Skips the rest of this exercise, not just this set.
   *
   * "I can't do this one" is the realistic action; making an athlete tap Skip once per set to express
   * it would be busywork. Every remaining set is recorded as skipped so the coach sees how far they
   * got rather than an exercise that simply vanishes from the log.
   */
  function handleSkip() {
    if (flagged && !flagSent) {
      sendFlag.mutate({ note: flagNote, sessionEnded: false });
    }
    skipRemainingSetsOfCurrentExercise(flagged);
  }

  /**
   * What happens to THIS exercise once it has been reported as hurting, while the session keeps going.
   *
   * Standalone, this is exactly a Skip: there is nowhere else this exercise's index will ever come up
   * again, so every remaining set of it is abandoned right now. Inside a circuit it is different --
   * the exercise recurs every round, and the OTHER exercises in the circuit are meant to carry on
   * untouched (27 Sep debug notes), so only THIS round's instance is recorded as skipped here, using
   * the position the plan would have moved to anyway; the circuit's own round-by-round logic decides
   * whether more rounds follow. Marking the index as injured is what makes every later round skip it
   * automatically too, via the effect below, without repeating the report each time it comes back around.
   */
  function stopExerciseDueToInjury() {
    setInjuredElementIndexes((previous) => {
      const next = new Set(previous);
      next.add(currentIndex);
      return next;
    });

    if (!currentGroup) {
      skipRemainingSetsOfCurrentExercise(true);
      return;
    }

    const wasLastRound = currentSet + 1 >= repetitions;
    const entry: LoggedExercise = {
      elementIndex: currentIndex,
      setIndex: currentSet,
      status: "skipped",
      reps: null,
      weight: null,
      weightUnit: null,
      durationSeconds: null,
      flagged: true,
      flagNote: flagNote || null,
    };
    const nextLog = [...log, entry];
    setLog(nextLog);
    const position = nextPosition(currentIndex, wasLastRound);
    resetExerciseForm();
    advanceAfterSet(currentIndex, wasLastRound);
    persistProgress(nextLog, position);
  }

  /** Picks a saved session back up exactly where it stopped. */
  function resumeWorkout(saved: SessionDraft) {
    setLog(saved.exercises);
    // Answered when the workout began. A resume doesn't ask again.
    setCheckin(saved.checkin ? toAnswers(saved.checkin) : null);
    setCurrentIndex(saved.current_index);
    setCurrentSet(saved.current_set);
    resetExerciseForm();
    // Straight into the work, with no 3-2-1: they are already warmed up and mid-session, and a countdown
    // would imply this is a fresh start.
    setPhase("working");
  }

  /** How long ago a saved session was last touched, in the words a person would use. */
  function describeAge(iso: string): string {
    const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
    if (minutes < 1) return "just now";
    if (minutes === 1) return "1 minute ago";
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.round(minutes / 60);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  /**
   * What the closing screen reports back. Counted from the log rather than from the plan, so it says
   * what was actually done -- including the skips, which are a fact about the session and not a failure
   * to hide from the person who made the honest call to skip.
   */
  /** A hold currently running, which takes the whole screen the way a rest does. */
  const holding = holdSecondsLeft !== null && holdSecondsLeft > 0;

  const completedSets = log.filter((entry) => entry.status === "completed").length;
  const skippedSets = log.filter((entry) => entry.status === "skipped").length;
  const totalReps = log.reduce((sum, entry) => sum + (entry.reps ?? 0), 0);

  const actionError = logSession.error;
  const noPlan = planQuery.isError && isNotFound(planQuery.error);

  if (phase === "overview") {
    return (
      <FamilyWorkspace
        clientId={clientId}
        athletes={clientsQuery.data ?? []}
        thisClient={thisClient}
        canWrite={canWrite}
        plan={plan ?? null}
        planPending={planQuery.isPending}
        planError={planQuery.isError && !noPlan ? planQuery.error : null}
        onRetryPlan={() => planQuery.refetch()}
        noPlan={noPlan}
        elements={elements}
        planBody={planBody}
        actionError={actionError}
        onStart={() => setPhase("checkin")}
        resumeCard={
          resumable && canWrite ? (
                        <div className="walkthrough-card resume-card" role="status">
                          <p className="stage-progress">You have a session in progress</p>
                          <p className="form-hint">
                            You started {describeAge(resumable.started_at)} and logged {resumable.exercises.length}{" "}
                            {resumable.exercises.length === 1 ? "set" : "sets"}. Nothing has been lost.
                          </p>
                          <button type="button" onClick={() => resumeWorkout(resumable)}>
                            Carry on where I left off
                          </button>
                          <button
                            type="button"
                            className="chip"
                            disabled={discardDraft.isPending}
                            onClick={() => {
                              discardDraft.mutate();
                              setPhase("checkin");
                            }}
                          >
                            {discardDraft.isPending ? "Starting…" : "Start this session again"}
                          </button>
                        </div>
          ) : null
        }
      />
    );
  }

  return (
    <PageShell
      title="Training walkthrough"
      // No confirmation on the way out any more. It used to warn that leaving would lose the session,
      // which was true then and is not true now -- every completed set is already on the server, and the
      // overview offers to pick it back up.
      actions={
        <Link className="link-button" to="/family">
          Back
        </Link>
      }
    >
      {actionError && <ErrorState error={actionError} />}
      {planQuery.isError && !noPlan && <ErrorState error={planQuery.error} onRetry={() => planQuery.refetch()} />}

      {planQuery.isPending && <LoadingState label="Loading your plan…" />}

      {noPlan && <p className="empty-state">No plan has been published for you yet.</p>}

      {plan && (
        <>
          <h2>{plan.title}</h2>

          {phase === "checkin" && (
            <CheckIn
              athleteName={thisClient?.name}
              onTrain={trainAfterCheckin}
              onRest={restAfterCheckin}
              sending={sendCheckinFlag.isPending}
              error={sendCheckinFlag.error}
            />
          )}

          {phase === "rested" && (
            <div className="fullscreen-stage">
              <p className="stage-sub">Rest up today.</p>
              <p className="stage-sub">Your coach will see what you told them next time they look at your page.</p>
              <button type="button" onClick={() => navigate("/family")}>
                Back to my training
              </button>
            </div>
          )}

          {/* Full-screen 3-2-1. The point is the moment of committing: a workout should start, not
              just appear. */}
          {phase === "countdown" && (
            <div className="fullscreen-stage" role="status" aria-live="assertive">
              <p className="stage-label">Get ready</p>
              <p className="stage-number">{countdown > 0 ? countdown : "GO!"}</p>
              <p className="stage-sub">
                {elements[0]?.name}
                {groupOf(planBody, elements[0])
                  ? " · Round 1"
                  : setsFor(elements[0]) > 1
                    ? " · Set 1"
                    : ""}
              </p>
            </div>
          )}

          {/* Rest takes the whole screen, as in the prototype. Between exercises it previews what is
              coming, so the athlete can watch the demo instead of staring at a number. */}
          {phase === "resting" && (
            <div className="fullscreen-stage" role="status" aria-live="polite">
              <p className="stage-label">
                {restingBetweenSets ? (currentGroup ? "Rest before your next round" : "Rest before your next set") : "Rest"}
              </p>
              <p className="stage-number">{restSecondsLeft ?? 0}s</p>
              {restingBetweenSets ? (
                <p className="stage-sub">
                  {currentGroup
                    ? `${currentGroup.name ?? "Circuit"} · Round ${currentSet + 1} of ${repetitions}`
                    : `${current?.name} · Set ${currentSet + 1} of ${repetitions}`}
                </p>
              ) : (
                current && (
                  <div className="stage-preview">
                    <p className="stage-sub">Next up: {current.name}</p>
                    {/* Converted into the athlete's own unit, matching the working screen below and
                        the "Coach said" line -- otherwise the preview and the actual prompt could
                        disagree about the same number. */}
                    {describePrescription(current, false, weightUnit) && (
                      <p className="element-prescription">{describePrescription(current, false, weightUnit)}</p>
                    )}
                    <LibraryCues entryId={current.libraryEntryId} />
                    {current.notes && <p className="element-notes">{String(current.notes)}</p>}
                    {currentVideoUrl && !videoFailed && (
                      // eslint-disable-next-line jsx-a11y/media-has-caption
                      <video controls src={currentVideoUrl} className="library-video" onError={() => setVideoFailed(true)} />
                    )}
                  </div>
                )
              )}
              <button type="button" onClick={endRest}>
                Skip rest
              </button>
            </div>
          )}

          {/* Between exercises, and the big one at the end. Short and loud on purpose: this is the bit
              that makes finishing feel like finishing. */}
          {phase === "celebrating" && (
            <div className="fullscreen-stage" role="status" aria-live="assertive">
              <p className="stage-number stage-celebrate">
                {currentIndex >= elements.length ? "WORKOUT DONE!" : "NICE WORK!"}
              </p>
              <p className="stage-sub">
                {currentIndex >= elements.length
                  ? "That is the whole session."
                  : `${elements[currentIndex - 1]?.name} complete.`}
              </p>
            </div>
          )}

          {/*
            A running hold takes the whole screen, exactly as a rest does.

            Both countdowns are full-screen on purpose. The rest one is a deliberate requirement -- a
            full-screen counter that previews the next exercise, so the pause is useful rather than just
            elapsed -- and the hold had no business being smaller: it is read face-down on the floor
            mid-plank, squinting sideways at a propped-up phone, which is the harder of the two to read
            (UX review F63). It replaces the working card rather than overlaying it, for the same reason
            the countdown and celebration are their own screens: an athlete mid-effort should never have
            a timer competing with the exercise they are doing.
          */}
          {phase === "working" && current && holding && (
            <div className="fullscreen-stage" role="status" aria-live="off">
              <p className="stage-label">Hold it</p>
              <p className="stage-number">{holdSecondsLeft}s</p>
              <p className="stage-sub">{current.name}</p>
              <button type="button" onClick={() => setHoldSecondsLeft(null)}>
                Stop
              </button>
            </div>
          )}

          {phase === "working" && current && !holding && (
            <div className="walkthrough-card">
              {/* Big, because knowing where you are mid-workout matters more than anything else on this
                  screen -- it used to be a 12px label above the exercise name. */}
              {/* Inside a circuit the round is the thing that matters -- "exercise 3 of 8, set 2 of 3"
                  tells an athlete nothing about where they are in the loop they are actually doing. */}
              {currentGroup ? (
                /* The round is what matters inside a circuit, but it used to REPLACE the overall
                   position for the whole circuit -- so "how much longer", the defining question, became
                   unanswerable exactly where it is hardest to eyeball: six blocks that all look the
                   same (UX review F64). Both are shown now, the round first. */
                <p className="stage-progress">
                  {currentGroup.name ?? "Circuit"}
                  <span className="stage-progress-set">
                    Round {currentSet + 1}/{repetitions}
                  </span>
                  <span className="stage-progress-overall">
                    Exercise {currentIndex + 1}/{elements.length}
                  </span>
                </p>
              ) : (
                <p className="stage-progress">
                  Exercise {currentIndex + 1}/{elements.length}
                  {repetitions > 1 && (
                    <span className="stage-progress-set">
                      Set {currentSet + 1}/{repetitions}
                    </span>
                  )}
                </p>
              )}
              <p className="exercise-name">{current.name}</p>
              {/* Converted into the athlete's own unit -- see the note by the "Coach said" line for
                  why a raw, unconverted number was the reported bug. */}
              {describePrescription(current, Boolean(currentGroup), weightUnit) && (
                <p className="element-prescription">
                  {describePrescription(current, Boolean(currentGroup), weightUnit)}
                </p>
              )}
              {/* The coach's cues, at the moment they are for -- the athlete is about to do the
                  exercise. The library's cues are read live from the entry rather than copied into the
                  plan, so a cue the coach fixed last night is the one shown this morning; anything
                  written for this athlete specifically sits underneath it. */}
              <LibraryCues entryId={current.libraryEntryId} />
              {current.notes && <p className="element-notes">{String(current.notes)}</p>}
              <LastTime exerciseName={current.name} history={historyFor(current, exerciseHistory)} />
              {currentVideoUrl && !videoFailed && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video controls src={currentVideoUrl} className="library-video" onError={() => setVideoFailed(true)} />
              )}
              {currentVideoUrl && videoFailed && <p className="error">The demo video for this exercise could not be loaded.</p>}

              {/* A hold is timed by the app rather than counted by the athlete -- "30 seconds" is the one
                  prescription you cannot self-pace, and guessing it defeats the point of prescribing it. */}
              {measureOf(current) === "time" && current.durationSeconds ? (
                <div className="hold-timer">
                  {holdSecondsLeft === null ? (
                    <button type="button" onClick={() => setHoldSecondsLeft(current.durationSeconds!)}>
                      Start {formatSeconds(current.durationSeconds)} hold
                    </button>
                  ) : (
                    <p role="status" className="badge badge-ok">
                      Hold complete
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {/* The target sits next to the box, and the box asks for what actually happened.
                      "Reps" alone is ambiguous enough that athletes type the prescription back. */}
                  <label htmlFor="reps">
                    How many reps did you actually do?
                    {current.reps ? <span className="field-target">Target: {current.reps}</span> : null}
                  </label>
                  {/* The target is the VALUE, not a placeholder. As a placeholder it looked exactly like
                      a filled-in number, so athletes tapped Complete without touching it and nothing was
                      submitted -- 12 of 15 entries in a normally-completed session came back to the coach
                      as "Completed, nothing recorded" (UX review F74). Hitting the prescription is the
                      common case; typing is now only needed when it differed. */}
                  <input
                    id="reps"
                    type="number"
                    inputMode="numeric"
                    value={reps}
                    onChange={(e) => setReps(e.target.value)}
                  />
                </>
              )}

              {/*
                Asked only where the coach actually prescribed a load, or where the athlete says there
                was one. It used to appear on everything -- so a nine-year-old was asked how much weight
                she used to do squat jumps, and then a plank, with "lb" pre-selected for an answer she
                had never given (UX review F62). "I used weight for this" opens it for the case the coach
                didn't prescribe but the athlete did.
              */}
              {hasPrescribedLoad(current) || weightPromptOpen ? (
                <>
                  <label htmlFor="weight">
                    What weight did you actually use?
                    {/*
                      Converted into the unit the athlete is actually logging in, not the coach's raw
                      number relabelled. A coach prescribing "10" on lb used to reach a kg-preferring
                      athlete as "Coach said: 10 kg" -- the athlete's own chip selection appended to the
                      coach's number rather than converting it, wrong in the dangerous direction for a
                      child loading a bar. `describeLoad` converts properly when the prescription is
                      structured; a legacy free-text load is shown exactly as typed, with no unit
                      appended, since we cannot know what unit it was in.
                    */}
                    {hasPrescribedLoad(current) ? (
                      <span className="field-target">Coach said: {describeLoad(current, weightUnit)}</span>
                    ) : null}
                  </label>
                  <div className="filter-chips" role="group" aria-label="Weight unit">
                    {(["bodyweight", "lb", "kg"] as const).map((unit) => (
                      <button
                        key={unit}
                        type="button"
                        className={`chip ${weightUnit === unit ? "active" : ""}`}
                        aria-pressed={weightUnit === unit}
                        onClick={() => handleWeightUnitChange(unit)}
                      >
                        {unit === "bodyweight" ? "Bodyweight" : unit}
                      </button>
                    ))}
                  </div>
                  {/* Bodyweight is an answer, not a missing number, so the box goes away rather than
                      sitting there empty inviting a zero. */}
                  {weightUnit !== "bodyweight" && (
                    <input
                      id="weight"
                      type="number"
                      inputMode="decimal"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                    />
                  )}
                </>
              ) : (
                <button type="button" className="chip" onClick={() => setWeightPromptOpen(true)}>
                  I used weight for this
                </button>
              )}

              {/* Ticking this used to reveal a box and then move on to the next set -- no acknowledgement,
                  no offer to stop, and the report itself only reached the coach if the whole session was
                  finished and rated. The person who has just reported pain was invited to do it again
                  (UX review F72). */}
              {/* A real button, not a ~13px unstyled checkbox below the weight box. The mechanism
                  existed but was nearly invisible, which is the quiet way a safety-relevant signal
                  never gets sent -- Maya would not know what it was for and Devon would not bother
                  (UX review F67). Still a toggle, so it can be un-picked without reloading. */}
              <button
                type="button"
                className={flagged ? "report-issue active" : "report-issue"}
                aria-pressed={flagged}
                onClick={() => setFlagged((was) => !was)}
              >
                Something hurt or went wrong
              </button>
              {flagged && (
                <div className="flag-block">
                  <textarea
                    aria-label="Flag note"
                    value={flagNote}
                    onChange={(e) => setFlagNote(e.target.value)}
                    placeholder="What happened?"
                  />
                  {sendFlag.isError && <ErrorState error={sendFlag.error} />}
                  {flagSent ? (
                    <p role="status" className="badge badge-ok">
                      Sent to your coach.
                    </p>
                  ) : (
                    <>
                      {/* Both of these send it now. The choice is only about whether to keep training. */}
                      <button
                        type="button"
                        disabled={sendFlag.isPending}
                        onClick={() => reportIssue({ stop: false })}
                      >
                        {sendFlag.isPending ? "Sending…" : "Tell my coach and keep going"}
                      </button>
                      <button
                        type="button"
                        className="chip"
                        disabled={sendFlag.isPending || logSession.isPending}
                        onClick={() => reportIssue({ stop: true })}
                      >
                        Tell my coach and stop for today
                      </button>
                    </>
                  )}
                  {flagSent && (
                    <button
                      type="button"
                      className="chip"
                      disabled={logSession.isPending}
                      onClick={() => endSessionEarly()}
                    >
                      {logSession.isPending ? "Finishing…" : "Stop for today"}
                    </button>
                  )}
                </div>
              )}

              <button onClick={handleComplete}>Complete</button>
              <button type="button" className="chip" onClick={handleSkip}>
                Skip
              </button>

              {/* The reassurance that replaces the browser's "Leave site?" dialog (F71). It is only worth
                  saying once there is something saved to reassure them about. */}
              {log.length > 0 && (
                <p className="form-hint">
                  Saved as you go &mdash; you can stop and pick this up later.
                </p>
              )}
            </div>
          )}

          {/* One RPE, at the end, with the scale spelled out. Asked here rather than per set because the
              honest number is the one you give having finished, not the one you give mid-set. */}
          {phase === "rating" && (
            <div className="walkthrough-card">
              <h2>How hard was that?</h2>
              <p className="form-hint">
                RPE means &ldquo;rate of perceived exertion&rdquo; &mdash; how hard the whole session felt
                to you. There is no right answer, and your coach uses it to plan next week.
              </p>
              <div className="rpe-scale" role="group" aria-label="RPE scale">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={sessionRpe === value ? "chip active" : "chip"}
                    onClick={() => setSessionRpe(value)}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <p className="rpe-legend">
                <span>1 &mdash; very easy</span>
                <span>10 &mdash; as hard as you could go</span>
              </p>

              {logSession.isError && <ErrorState error={logSession.error} />}
              <button onClick={() => logSession.mutate(log)} disabled={logSession.isPending || sessionRpe === null}>
                {logSession.isPending ? "Saving…" : "Finish session"}
              </button>
              {sessionRpe === null && <p className="form-hint">Pick a number to finish.</p>}
            </div>
          )}

          {phase === "done" && (
            <div className="fullscreen-stage">
              {/* Stopping because something hurt is the right decision, and the closing screen says so
                  rather than congratulating them on a workout they did not finish. */}
              {stoppedEarly ? (
                <>
                  <p className="stage-sub">You stopped, and told your coach why.</p>
                  <p className="stage-sub">
                    That was the right thing to do. Rest it, and your coach will pick this up with you.
                  </p>
                </>
              ) : (
                <>
                  <p className="stage-number stage-celebrate">SESSION DONE!</p>
                  {/* "Effort in, nothing out" is Devon's listed quit condition, and the end of a session
                      was exactly that: "SESSION LOGGED", a button, and no sign of what had just been
                      done -- not even the RPE that had been carefully explained and collected one screen
                      earlier (UX review F65). */}
                  <ul className="session-summary">
                    <li>
                      <span className="summary-number">{completedSets}</span>
                      <span className="summary-label">{completedSets === 1 ? "set done" : "sets done"}</span>
                    </li>
                    {totalReps > 0 && (
                      <li>
                        <span className="summary-number">{totalReps}</span>
                        <span className="summary-label">reps</span>
                      </li>
                    )}
                    {sessionRpe !== null && (
                      <li>
                        <span className="summary-number">{sessionRpe}</span>
                        <span className="summary-label">how hard it felt</span>
                      </li>
                    )}
                  </ul>
                  {skippedSets > 0 && (
                    <p className="stage-sub">
                      {skippedSets} skipped &mdash; that is fine, your coach can see what you got through.
                    </p>
                  )}
                  <p className="stage-sub">Your coach can see it now.</p>
                </>
              )}
              <button type="button" onClick={() => navigate("/family")}>
                Back to my training
              </button>
            </div>
          )}
        </>
      )}

    </PageShell>
  );
}

const FAMILY_TABS = ["today", "plan", "goals", "history", "login"] as const;
type FamilyTabId = (typeof FAMILY_TABS)[number];

/**
 * One athlete's training, for their family -- everything before a workout starts (Oct 2026 redesign).
 *
 * The plan, a Start button, the goals and the whole session history used to be one scroll on this page,
 * with each athlete reached from a list on the home screen. It is now a workspace like the coach's: a
 * switcher between a guardian's athletes, what's new, then Today (the workout to do now, and how it's
 * going), Plan, Goals, History, and -- for a guardian of a minor -- the minor's own Login.
 *
 * Once a workout starts the page is the guided session alone, as it always was.
 */
function FamilyWorkspace({
  clientId,
  athletes,
  thisClient,
  canWrite,
  plan,
  planPending,
  planError,
  onRetryPlan,
  noPlan,
  elements,
  planBody,
  actionError,
  onStart,
  resumeCard,
}: {
  clientId: string | undefined;
  athletes: FamilyClientListItem[];
  thisClient: FamilyClientListItem | undefined;
  canWrite: boolean;
  plan: PlanRow | null;
  planPending: boolean;
  planError: unknown;
  onRetryPlan: () => void;
  noPlan: boolean;
  elements: PlanElement[];
  planBody: PlanBody;
  actionError: unknown;
  onStart: () => void;
  resumeCard: ReactNode;
}) {
  const isAthleteLogin = useAccountStatus().data?.access === "not_applicable";
  // A guardian's business, about a minor: their own login. An athlete never manages their own.
  const showLogin = !isAthleteLogin && Boolean(thisClient?.is_minor);
  const tabIds = showLogin ? FAMILY_TABS : FAMILY_TABS.filter((id) => id !== "login");
  const { active, select, isMounted } = useWorkspaceTab(tabIds);
  const narrow = useIsNarrow();

  const unread = useUnreadMessages();
  const hasUnread = Boolean(clientId && unread.data?.client_ids?.includes(clientId));
  const billing = useFamilyBilling(undefined, !isAthleteLogin);
  const athleteBilling = billing.data?.athletes?.find((entry) => entry.client_id === clientId);
  const sessions = useSessions(clientId).data ?? [];
  const goals = useGoals<GoalRow[]>(clientId).data ?? [];

  const firstName = thisClient?.name.split(" ")[0] ?? null;
  // Neutral until the athlete list arrives, so a guardian never briefly reads "Your training".
  const headline = isAthleteLogin ? "Your training" : firstName ? `${firstName}’s training` : "Training";
  const canStart = canWrite && Boolean(plan) && elements.length > 0;

  const request = athleteBilling?.plan_request;
  const requestNews = Boolean(request && (request.status === "pending" || !request.family_seen));
  const hasNews = hasUnread || requestNews;

  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recent = sessions.filter((session) => Date.parse(session.completed_at) >= weekAgo);
  const achieved = goals.filter((goal) => goal.achieved_at).length;
  const nextGoal = goals
    .filter((goal) => !goal.achieved_at)
    .sort((a, b) => (a.target_date ?? "9999").localeCompare(b.target_date ?? "9999"))[0];

  const tabs: WorkspaceTab<FamilyTabId>[] = [
    { id: "today", label: "Today" },
    { id: "plan", label: "Plan" },
    { id: "goals", label: "Goals" },
    { id: "history", label: "History" },
    ...(showLogin ? [{ id: "login" as const, label: "Login" }] : []),
  ];

  const messagesLink = clientId ? (
    <Link className="link-button" to={`/family/clients/${clientId}/messages`}>
      Messages
      {hasUnread && <span className="nav-unread-dot" aria-label="Unread messages" />}
    </Link>
  ) : null;

  return (
    <div className="dashboard workspace">
      {/* Between a guardian's athletes, without going back to a list. An athlete's own login has one. */}
      {!isAthleteLogin && athletes.length > 1 && (
        <nav className="athlete-switcher" aria-label="Your athletes">
          {athletes.map((athlete) => (
            <Link
              key={athlete.id}
              to={`/family/clients/${athlete.id}/plan`}
              className={athlete.id === clientId ? "athlete-pill active" : "athlete-pill"}
              aria-current={athlete.id === clientId ? "page" : undefined}
            >
              <span className="client-avatar athlete-pill-avatar">{initials(athlete.name)}</span>
              {athlete.name}
              {unread.data?.client_ids?.includes(athlete.id) && <span className="nav-unread-dot" aria-label="Unread messages" />}
            </Link>
          ))}
        </nav>
      )}

      <header className="workspace-header">
        {!isAthleteLogin && (
          <Link className="workspace-back" to="/family">
            ‹ Home
          </Link>
        )}
        <div className="workspace-identity">
          <h1>{headline}</h1>
          {!narrow && messagesLink && <div className="header-actions">{messagesLink}</div>}
        </div>
        {!canWrite && firstName && (
          <div className="workspace-chips">
            <span className="badge">{firstName} logs their own workouts</span>
          </div>
        )}
      </header>

      {hasNews && (
        <section className="family-news" aria-labelledby="family-news-heading">
          <h2 id="family-news-heading" className="summary-label">
            New for you
          </h2>
          {hasUnread && clientId && (
            <div className="summary-card news-card">
              <p className="summary-note">There&rsquo;s a new message from your coach.</p>
              <Link className="link-button" to={`/family/clients/${clientId}/messages`}>
                Open messages
              </Link>
            </div>
          )}
          {athleteBilling && <PlanRequestStatus athlete={athleteBilling} />}
        </section>
      )}

      {actionError ? <ErrorState error={actionError} /> : null}

      <WorkspaceTabs tabs={tabs} active={active} onSelect={select} label="Training sections" />

      <WorkspacePanel id="today" active={active} mounted={isMounted("today")}>
        {planError ? <ErrorState error={planError} onRetry={onRetryPlan} /> : null}
        {planPending && <LoadingState label="Loading your plan…" />}
        {noPlan && <p className="empty-state">No plan has been published for you yet.</p>}

        {/* A session that was interrupted is offered back rather than silently discarded (F70). */}
        {resumeCard}

        {plan && (
          <div className="today-hero">
            <div className="today-hero-text">
              <p className="summary-label">Today&rsquo;s workout</p>
              <h2>{plan.title}</h2>
              {/* "How long will this take?" decides whether a nine-year-old starts at all (F56). */}
              {describeSessionLength(planBody) && <p className="session-estimate">{describeSessionLength(planBody)}</p>}
            </div>
            {canWrite ? (
              <button type="button" onClick={onStart} disabled={elements.length === 0}>
                {elements.length === 0 ? "Nothing to do in this plan" : "Start workout"}
              </button>
            ) : (
              // Says why, rather than just hiding the button: a guardian who could log sessions yesterday
              // needs to know this changed because their athlete now has their own login.
              <p className="empty-state">
                This athlete logs their own sessions now. You can see their plan and progress here, but only
                they can start a session.
              </p>
            )}
          </div>
        )}

        <div className="summary-grid">
          <article className="summary-card">
            <p className="summary-label">Last 7 days</p>
            <div className="summary-stats">
              <div>
                <span className="summary-stat">{recent.length}</span>
                <span className="form-hint">{recent.length === 1 ? "workout" : "workouts"}</span>
              </div>
            </div>
            <button type="button" className="chip" onClick={() => select("history")}>
              See history
            </button>
          </article>
          <article className="summary-card">
            <p className="summary-label">
              Working towards{goals.length ? ` · ${achieved} of ${goals.length} achieved` : ""}
            </p>
            {nextGoal ? (
              <>
                <h3>{nextGoal.text}</h3>
                <p className="form-hint">
                  {nextGoal.target_date ? `By ${formatTargetDate(nextGoal.target_date)}` : "No target date yet"}
                </p>
              </>
            ) : (
              <p className="form-hint">{goals.length ? "Every goal achieved." : "No goals yet."}</p>
            )}
            <button type="button" className="chip" onClick={() => select("goals")}>
              See goals
            </button>
          </article>
        </div>
      </WorkspacePanel>

      {/* What is in the workout, before starting it. */}
      <WorkspacePanel id="plan" active={active} mounted={isMounted("plan")}>
        {plan ? (
          <div className="walkthrough-card">
            <h2>{plan.title}</h2>
            <PlanDetail elements={elements} groups={planBody.groups} />
            {canStart && (
              <button type="button" onClick={onStart}>
                Start workout
              </button>
            )}
          </div>
        ) : (
          <p className="empty-state">No plan has been published for you yet.</p>
        )}
      </WorkspacePanel>

      {/* Client-scoped, not plan-scoped (27 Sep debug notes item 8). Read access survives the minor
          getting their own login (PRD §2.3); ClientGoals withholds the writes when canWrite is false. */}
      <WorkspacePanel id="goals" active={active} mounted={isMounted("goals")}>
        {clientId && <ClientGoals clientId={clientId} canWrite={canWrite} />}
      </WorkspacePanel>

      <WorkspacePanel id="history" active={active} mounted={isMounted("history")}>
        <ProgressChart sessions={sessions} />
        {clientId && (
          <SessionHistory
            clientId={clientId}
            elementNamesByPlan={plan ? { [plan.id]: elements.map((element) => element.name) } : undefined}
            detailBase="/family/sessions"
          />
        )}
      </WorkspacePanel>

      {showLogin && thisClient && (
        <WorkspacePanel id="login" active={active} mounted={isMounted("login")}>
          <h2>{thisClient.name}&rsquo;s own login</h2>
          {thisClient.skater_login_id === null ? (
            <SkaterLoginForm clientId={thisClient.id} clientName={thisClient.name} />
          ) : (
            <SkaterLoginDetails
              clientId={thisClient.id}
              clientName={thisClient.name}
              username={thisClient.skater_login_username}
              createdAt={thisClient.skater_login_created_at}
              passwordResetAt={thisClient.skater_login_password_reset_at}
            />
          )}
        </WorkspacePanel>
      )}

      {/* On a phone, starting and messaging stay under the thumb. */}
      {narrow && (
        <div className="workspace-bottom-bar">
          {messagesLink}
          {canStart && (
            <button type="button" className="workspace-primary" onClick={onStart}>
              Start workout
            </button>
          )}
        </div>
      )}
    </div>
  );
}
